import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash, FaEllipsisH, FaChartLine } from 'react-icons/fa';
import { Wifi, Wallet, TrendingUp, Globe, ChevronLeft, Settings, AlertTriangle } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import {
  AreaChart, Area, XAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, YAxis, CartesianGrid, LabelList
} from 'recharts';
import toast from 'react-hot-toast';
import TableView from '../TableView';
import { QuickActionsMenu } from '../common';
import EditableTextCell from './EditableTextCell';
import CustomSelect from './CustomSelect';
import ClearMonthModal from './ClearMonthModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import GastosPorCentroCosto from './GastosPorCentroCosto';
import TendenciaAnualModal from './TendenciaAnualModal';
import '../AssetsView.css';
import { SURA_BLUE, SURA_BLUE2, SURA_TEAL, mesesVisibles, serieAnualItem } from './palette';

const DEPT_COLORS = [
  '#003366','#0062A5','#009AB3','#00B5C8','#2DC4D6',
  '#0080C0','#1E7B8C','#3BACC4','#5B8DB8','#00657A'
];
const CENTROS_COSTO_OPTIONS = [
  '001 - Centro de Distribución', '002 - Share Service', '003 - Presidencia',
  '004 - Gerencia General', '005 - Finanzas', '006 - Gestion Humana',
  '007 - TI', '008 - Mercadeo', '009 - Comercial', '010 - Servicios Generales',
  '011 - Almacen', '012 - Distribucion', '013 - Seguridad Industrial',
  '014 - Calidad', '015 - Supply Chain', '016 - Comercio Exterior',
  '017 - Transporte', '018 - Trade Marketing', '019 - PCP'
];

const SEDES_OPTIONS = ['CA1', 'CDN', 'Presidencia', 'Almacén', 'Planta'];

/* ─── Custom Tooltip ───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[140px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-3 py-0.5">
          <span className="text-xs text-gray-600">Gasto</span>
          <span className="text-xs font-bold text-gray-900">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const PresupuestoInternetView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concepto, setConcepto] = useState(null);
  const [enlaces, setEnlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const mesesOrden = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
  const [mesSeleccionado, setMesSeleccionado] = useState(mesesOrden[new Date().getMonth()]);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [filtroDepartamento, setFiltroDepartamento] = useState('');
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [enlaceAEliminar, setEnlaceAEliminar] = useState(null);
  const [trendItem, setTrendItem] = useState(null); // { titulo, subtitulo, data }

  const [showAddModal, setShowAddModal] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState('');
  const [nuevaSede, setNuevaSede] = useState('');
  const [nuevoDepartamento, setNuevoDepartamento] = useState('');

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [sedeConfiguracion, setSedeConfiguracion] = useState('');
  const [distribuciones, setDistribuciones] = useState([]);
  const [distPorSede, setDistPorSede] = useState({});

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conceptosRes, enlacesRes] = await Promise.all([
        axiosInstance.get('/api/presupuestos/conceptos'),
        axiosInstance.get(`/api/presupuestos/internet/${id}`)
      ]);
      const currentConcepto = conceptosRes.data.find(c => c.id === parseInt(id));
      setConcepto(currentConcepto);
      setEnlaces(enlacesRes.data);
      await loadDistribuciones(enlacesRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleClearMonth = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmClearMonth = async () => {
    try {
      await axiosInstance.delete(`/api/presupuestos/clear/internet/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`);
      toast.success(`Datos de ${mesSeleccionado} eliminados correctamente.`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar los datos del mes');
    }
  };

  const loadDistribuciones = async (enlacesActuales) => {
    const sedes = [...new Set((enlacesActuales || enlaces).map(e => e.sede).filter(Boolean))];
    if (!sedes.length) { setDistPorSede({}); return; }
    try {
      const results = await Promise.all(sedes.map(s => axiosInstance.get('/api/presupuestos/internet/distribucion/' + s)));
      const map = {};
      sedes.forEach((s, i) => { map[s] = results[i].data || []; });
      setDistPorSede(map);
    } catch { /* mantiene distPorSede anterior si falla la carga */ }
  };

  const handleInlineSave = async (enlaceId, field, value) => {
    try {
      const res = await axiosInstance.put(`/api/presupuestos/internet/enlace/${enlaceId}`, { [field]: value });
      if (res.status === 200) {
        setEnlaces(prev => prev.map(l => l.id === enlaceId ? { ...l, [field]: value } : l));
        toast.success('Actualizado correctamente');
        return true;
      }
      return false;
    } catch { toast.error('Error al actualizar'); return false; }
  };

  const handleGuardarGastoMes = async (enlaceId, montoDolar) => {
    if (montoDolar === null || montoDolar === undefined || montoDolar === '') return;
    const formData = new FormData();
    formData.append('mes', mesSeleccionado);
    formData.append('anio', anioSeleccionado);
    formData.append('monto_dolar', montoDolar);
    try {
      await axiosInstance.post(`/api/presupuestos/internet/enlace/${enlaceId}/gasto`, formData);
      toast.success('Precio guardado');
      fetchData();
    } catch { toast.error('Error al guardar el precio'); }
  };

  const handleCreateEnlace = async () => {
    if (!nuevoProveedor) { toast.error('El proveedor es requerido'); return; }
    try {
      await axiosInstance.post(`/api/presupuestos/internet/${id}`, {
        proveedor: nuevoProveedor, sede: nuevaSede, departamento: nuevoDepartamento
      });
      toast.success('Enlace registrado');
      setShowAddModal(false);
      setNuevoProveedor(''); setNuevaSede(''); setNuevoDepartamento('');
      fetchData();
    } catch { toast.error('Error al registrar enlace'); }
  };

  const handleDeleteEnlace = async (enlaceId) => {
    try {
      await axiosInstance.delete(`/api/presupuestos/internet/enlace/${enlaceId}`);
      toast.success('Enlace eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); } finally { setEnlaceAEliminar(null); }
  };

  const openConfigModal = async (sede) => {
    if (!sede) { toast.error('El enlace debe tener una Sede asignada primero.'); return; }
    setSedeConfiguracion(sede);
    setDistribuciones([]);
    setShowConfigModal(true);
    try {
      const res = await axiosInstance.get('/api/presupuestos/internet/distribucion/' + sede);
      setDistribuciones(res.data?.length > 0 ? res.data : [{ departamento: '', cantidad_personas: 1 }]);
    } catch { toast.error('Error al cargar configuración de sede'); }
  };

  const handleSaveConfig = async () => {
    const validas = distribuciones.filter(d => d.departamento && Number(d.cantidad_personas) > 0);
    if (validas.length === 0) {
      toast.error('Agrega al menos un departamento con personas antes de guardar');
      return;
    }
    if (validas.length < distribuciones.length) {
      toast('Se ignoraron filas sin departamento seleccionado', { icon: '⚠️' });
    }
    const departamentosUnicos = new Set(validas.map(d => d.departamento));
    if (departamentosUnicos.size < validas.length) {
      toast.error('Hay un departamento repetido en la distribución');
      return;
    }
    try {
      await axiosInstance.post('/api/presupuestos/internet/distribucion/' + sedeConfiguracion, validas);
      toast.success('Distribución guardada');
      setShowConfigModal(false);
      fetchData();
    } catch { toast.error('Error al guardar distribución'); }
  };

  /* ─── Chart Data ─────────────────────────────────────────────── */
  const chartData = useMemo(() => {
    return mesesVisibles(anioSeleccionado).map(mes => {
      let total = 0;
      enlaces.forEach(e => {
        const g = e.gastos?.find(g => g.mes === mes && g.anio === anioSeleccionado);
        if (g) total += Number(g.monto_dolar || 0);
      });
      return { name: mes.substring(0, 3), fullMonth: mes, total };
    });
  }, [enlaces, anioSeleccionado]); // eslint-disable-line

  const gastoTotalAnual = useMemo(() => chartData.reduce((a, c) => a + c.total, 0), [chartData]);
  const gastoMesActual = useMemo(() => chartData.find(d => d.fullMonth === mesSeleccionado)?.total || 0, [chartData, mesSeleccionado]);

  const comparisonData = useMemo(() => {
    const idx = mesesOrden.indexOf(mesSeleccionado); // eslint-disable-line
    if (idx <= 0) return null;
    const totalAnterior = chartData[idx - 1]?.total || 0;
    const variacion = totalAnterior > 0 ? ((gastoMesActual - totalAnterior) / totalAnterior) * 100 : null;
    return {
      ultimoMes: { name: mesSeleccionado.substring(0, 3), total: gastoMesActual },
      mesAnterior: { name: mesesOrden[idx - 1].substring(0, 3), total: totalAnterior },
      variacion
    };
  }, [chartData, mesSeleccionado, gastoMesActual]); // eslint-disable-line

  // Distribucion por dept para el mes seleccionado
  const proveedorDistData = useMemo(() => {
    return enlaces.map(enlace => {
      const gasto = enlace.gastos?.find(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado);
      const monto = Number(gasto?.monto_dolar || 0);
      if (!monto) return null;
      const label = enlace.proveedor + (enlace.sede ? ` (${enlace.sede})` : '');
      if (enlace.tipo_distribucion === 'PRORRATEO') {
        const dists = distPorSede[enlace.sede] || [];
        const totalPersonas = dists.reduce((s, d) => s + (d.cantidad_personas || 0), 0);
        if (!totalPersonas) return { label, items: [{ name: enlace.sede || 'Sin sede', value: monto, pct: 100 }] };
        return {
          label,
          items: dists.map(d => {
            const pct = parseFloat((d.cantidad_personas / totalPersonas * 100).toFixed(1));
            return { name: d.departamento || 'Sin nombre', value: parseFloat((monto * d.cantidad_personas / totalPersonas).toFixed(2)), pct };
          }).filter(i => i.value > 0)
        };
      }
      return { label, items: [{ name: enlace.departamento || enlace.sede || 'General', value: monto, pct: 100 }] };
    }).filter(Boolean);
  }, [enlaces, mesSeleccionado, anioSeleccionado, distPorSede]);

  /* Gastos por centro de costo del mes/año seleccionado — suma los montos
     ya prorrateados por departamento/sede (mismo cálculo que la gráfica de
     distribución por proveedor de arriba), pero agrupados en un único ranking */
  const ccData = useMemo(() => {
    const map = {};
    proveedorDistData.forEach(prov => {
      prov.items.forEach(item => {
        const cc = item.name || 'Sin asignar';
        map[cc] = (map[cc] || 0) + item.value;
      });
    });
    return Object.entries(map)
      .map(([centro_costo, total]) => ({ centro_costo, total }))
      .sort((a, b) => b.total - a.total);
  }, [proveedorDistData]);

  /* ─── Columns ────────────────────────────────────────────────── */
  const columns = useMemo(() => [
    {
      header: 'Proveedor',
      accessorKey: 'proveedor',
      cell: ({ row }) => (
        <div className="flex items-center gap-2 font-medium">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${SURA_BLUE}12` }}>
            <Wifi size={14} style={{ color: SURA_BLUE2 }} />
          </div>
          <EditableTextCell value={row.original.proveedor} onSave={(val) => handleInlineSave(row.original.id, 'proveedor', val)} />
        </div>
      )
    },
    {
      header: 'Sede',
      accessorKey: 'sede',
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.sede}
          onSave={(val) => handleInlineSave(row.original.id, 'sede', val)}
          options={SEDES_OPTIONS.map(s => ({ value: s, label: s }))}
          placeholder="Asignar sede..."
        />
      )
    },
    {
      header: 'Distribución',
      id: 'distribucion',
      cell: ({ row }) => {
        const isProrrateo = row.original.tipo_distribucion === 'PRORRATEO';
        const dists = distPorSede[row.original.sede] || [];
        const totalPersonasSede = dists.reduce((s, d) => s + (d.cantidad_personas || 0), 0);
        const sinConfigurar = isProrrateo && totalPersonasSede === 0;
        return (
          <div className="flex flex-col gap-2" style={{ minWidth: 180 }}>
            {/* Toggle pill */}
            <div className="flex rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style={{ fontSize: 11 }}>
              <button
                onClick={() => handleInlineSave(row.original.id, 'tipo_distribucion', 'DIRECTO')}
                className="flex-1 px-2 py-1.5 font-semibold transition-all"
                style={!isProrrateo ? { background: SURA_BLUE, color: '#fff' } : { background: 'transparent', color: '#9ca3af' }}
              >
                Directo
              </button>
              <button
                onClick={() => handleInlineSave(row.original.id, 'tipo_distribucion', 'PRORRATEO')}
                className="flex-1 px-2 py-1.5 font-semibold transition-all"
                style={isProrrateo ? { background: SURA_TEAL, color: '#fff' } : { background: 'transparent', color: '#9ca3af' }}
              >
                Por Sede
              </button>
            </div>
            {/* Segunda fila */}
            {isProrrateo ? (
              <button
                onClick={() => openConfigModal(row.original.sede)}
                className={`flex items-center justify-center gap-1.5 w-full py-1.5 rounded-lg text-xs font-bold transition-all border ${sinConfigurar ? 'animate-pulse' : ''}`}
                style={sinConfigurar
                  ? { background: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' }
                  : { background: `${SURA_TEAL}12`, color: SURA_TEAL, borderColor: `${SURA_TEAL}30` }}
                title={sinConfigurar ? 'Esta sede no tiene departamentos configurados: el 100% del costo se le asigna a la sede sin dividir' : undefined}
              >
                {sinConfigurar ? <AlertTriangle size={11} /> : <Settings size={11} />}
                {sinConfigurar ? `Sin configurar (${row.original.sede || '—'})` : `Configurar ${row.original.sede || '—'}`}
              </button>
            ) : (
              <EditableTextCell
                value={row.original.departamento}
                onSave={(val) => handleInlineSave(row.original.id, 'departamento', val)}
                options={CENTROS_COSTO_OPTIONS}
                placeholder="Seleccionar Dpto..."
              />
            )}
          </div>
        );
      }
    },
    {
      header: `Precio (${mesSeleccionado.substring(0, 3)})`,
      id: 'monto_dolar',
      cell: ({ row }) => {
        const gasto = row.original.gastos?.find(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado);
        const monto = gasto ? gasto.monto_dolar : '';
        return (
          <div className="flex items-center gap-1 font-semibold font-mono" style={{ color: SURA_TEAL }}>
            $
            <EditableTextCell value={monto} type="number" decimals={2} onSave={(val) => handleGuardarGastoMes(row.original.id, val)} placeholder="0.00" />
          </div>
        );
      }
    },
    {
      id: 'acciones', header: '', size: 50,
      cell: ({ row }) => (
        <div className="quick-actions-container" style={{ position: 'relative' }}>
          <button
            className={`icon-button ${activeActionMenu === row.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (activeActionMenu === row.id) {
                setActiveActionMenu(null);
                setMenuAnchorRect(null);
              } else {
                setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                setActiveActionMenu(row.id);
              }
            }}
          >
            <FaEllipsisH color="#111827" />
          </button>
          {activeActionMenu === row.id && (
            <QuickActionsMenu anchorRect={menuAnchorRect}>
              {[
                { icon: FaChartLine, label: 'Ver tendencia anual', color: SURA_BLUE2, onClick: () => { setTrendItem({ titulo: row.original.proveedor || row.original.sede || 'Enlace', subtitulo: [row.original.sede, row.original.departamento].filter(Boolean).join(' · '), data: serieAnualItem(row.original.gastos, anioSeleccionado, 'monto_dolar') }); setActiveActionMenu(null); } },
                { icon: Settings, label: 'Configurar Distribución', color: SURA_TEAL, onClick: () => { openConfigModal(row.original.sede); setActiveActionMenu(null); } },
                { icon: FaTrash, label: 'Eliminar Enlace', color: '#EF4444', onClick: () => { setEnlaceAEliminar(row.original.id); setActiveActionMenu(null); } }
              ].map((action) => (
                <button key={action.label} className="quick-action-item" onClick={action.onClick}>
                  <span className="action-icon" style={{ color: action.color }}><action.icon /></span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </QuickActionsMenu>
          )}
        </div>
      )
    }
  ], [mesSeleccionado, anioSeleccionado, enlaces, activeActionMenu, menuAnchorRect, distPorSede]); // eslint-disable-line

  const enlacesFiltrados = useMemo(() => {
    if (!filtroDepartamento) return enlaces;
    return enlaces.filter(e => e.departamento === filtroDepartamento || e.sede === filtroDepartamento);
  }, [enlaces, filtroDepartamento]);

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: SURA_BLUE }}></div></div>;
  if (!concepto) return <div>Concepto no encontrado</div>;

  return (
    <div className="assets-view">

      {/* ── Hero Header ──────────────────────────────────────────── */}
      <div
        className="rounded-2xl mb-6 px-7 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative"
        style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_TEAL} 100%)` }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute right-0 top-0 w-48 h-48 rounded-full opacity-10" style={{ background: '#fff', transform: 'translate(30%, -30%)' }} />
          <div className="absolute right-12 bottom-0 w-32 h-32 rounded-full opacity-5" style={{ background: '#fff', transform: 'translateY(40%)' }} />
        </div>

        <div className="flex items-center gap-4 z-10">
          <button onClick={() => navigate('/presupuestos')} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-medium transition-colors">
            <ChevronLeft size={16} /> Presupuestos
          </button>
          <div className="w-px h-5 bg-white/20" />
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <Globe size={22} color="#fff" />
          </div>
          <div>
            <h2 className="text-white text-xl font-bold m-0 leading-tight">{concepto.nombre}</h2>
            <p className="text-white/60 text-xs m-0 mt-0.5">Control de enlaces de internet y costos por sede</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 z-10">
          <CustomSelect className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }} value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)}>
            {mesesOrden.map(m => <option key={m} value={m}>{m}</option>)}
          </CustomSelect>
          <CustomSelect className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }} value={anioSeleccionado} onChange={e => setAnioSeleccionado(parseInt(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </CustomSelect>
          <button
            onClick={handleClearMonth}
            className="ml-1 bg-red-500/20 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors border border-red-500/30 hover:border-red-500 flex items-center justify-center group"
            title="Limpiar datos del mes"
          >
            <FaTrash size={15} className="text-red-300 group-hover:text-white transition-colors" />
          </button>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all" style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}>
            <FaPlus size={11} /> Nuevo Enlace
          </button>
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}>INTERNET</span>
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
          <div className="flex justify-between items-start mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Proveedores Activos</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <Wifi size={17} style={{ color: SURA_BLUE }} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{enlaces.length}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_TEAL }} />
          <div className="flex justify-between items-start mb-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Gasto Total ({anioSeleccionado})</p>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
              <Wallet size={17} style={{ color: SURA_TEAL }} />
            </div>
          </div>
          <p className="text-2xl font-bold font-mono text-gray-900">${gastoTotalAnual.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tendencia Anual</p>
            <TrendingUp size={14} style={{ color: SURA_TEAL }} />
          </div>
          <div className="h-16 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradInternet" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SURA_TEAL} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={SURA_TEAL} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke={SURA_TEAL} strokeWidth={2} fill="url(#gradInternet)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">vs Mes Anterior</p>
            <TrendingUp size={14} style={{ color: SURA_TEAL }} />
          </div>
          {comparisonData ? (
            <div className="flex items-end gap-2">
              <div className="text-center flex-1">
                <p className="text-[10px] text-gray-400 m-0 mb-1">{comparisonData.mesAnterior?.name}</p>
                <p className="text-xs font-semibold text-gray-500 m-0">${comparisonData.mesAnterior?.total.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="text-center flex-1">
                <p className="text-[10px] text-gray-400 m-0 mb-1">{comparisonData.ultimoMes.name}</p>
                <p className="text-xs font-bold text-gray-800 m-0">${comparisonData.ultimoMes.total.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
              </div>
              {comparisonData.variacion !== null && (
                <div className={`text-center flex-1 px-1 py-1 rounded-lg ${comparisonData.variacion > 5 ? 'bg-red-50 text-red-600' : comparisonData.variacion < -5 ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'}`}>
                  <p className="text-[10px] m-0 font-bold whitespace-nowrap">{comparisonData.variacion > 0 ? '▲' : '▼'} {Math.abs(comparisonData.variacion).toFixed(1)}%</p>
                </div>
              )}
            </div>
          ) : <p className="text-xs text-gray-400 mt-2">Sin datos suficientes</p>}
        </div>
      </div>


      {/* ── Gráfica Distribución por Departamento ────────────────── */}
      {proveedorDistData.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={15} style={{ color: SURA_TEAL }} />
            <span className="text-sm font-bold text-gray-700">Distribución por Departamento</span>
            <span className="text-xs text-gray-400">— {mesSeleccionado} {anioSeleccionado}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {proveedorDistData.map((prov, pIdx) => {
              const chartItems = [...prov.items].sort((a, b) => b.value - a.value);
              const barHeight = Math.max(90, chartItems.length * 28 + 40);
              return (
                <div key={pIdx} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${SURA_BLUE}06, ${SURA_TEAL}06)` }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg, ${SURA_BLUE}, ${SURA_TEAL})` }}>
                        <Wifi size={13} color="#fff" />
                      </div>
                      <span className="text-xs font-bold text-gray-800 truncate" title={prov.label}>{prov.label}</span>
                    </div>
                    <span className="text-xs font-black font-mono flex-shrink-0 ml-2" style={{ color: SURA_TEAL }}>
                      ${prov.items.reduce((s, i) => s + i.value, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Bar chart horizontal */}
                  <div className="px-3 pt-3 pb-2" style={{ height: barHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartItems} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }} barSize={10}>
                        <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#f3f4f6" />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={110}
                          tick={{ fontSize: 10, fill: '#6b7280', fontWeight: 500 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={v => v.length > 16 ? v.substring(0, 16) + '…' : v}
                        />
                        <XAxis type="number" hide />
                        <Tooltip
                          cursor={{ fill: 'transparent' }}
                          formatter={(val, name, props) => [`$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 })} (${props.payload.pct}%)`, 'Costo asignado']}
                          contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '11px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                          {chartItems.map((_, i) => (
                            <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />
                          ))}
                          <LabelList dataKey="pct" position="right" formatter={v => `${v}%`} style={{ fontSize: 10, fontWeight: 700, fill: '#6b7280' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Gastos por Centro de Costo ─────────────────────────── */}
      <div className="mb-6">
        <GastosPorCentroCosto
          data={ccData}
          subtitle={`${mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()} ${anioSeleccionado}`}
        />
      </div>

      {/* ── Tabla de Enlaces ───────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <TableView 
          data={enlacesFiltrados} 
          columns={columns} 
          searchPlaceholder="Buscar por proveedor, sede..." 
          customActions={
            <CustomSelect
              className="px-3 py-1.5 rounded-xl text-sm font-medium border-2 border-gray-200 outline-none text-gray-600 bg-white hover:border-gray-300 focus:border-sura-blue transition-colors cursor-pointer"
              value={filtroDepartamento}
              onChange={e => setFiltroDepartamento(e.target.value)}
            >
              <option value="">Todas las Sedes</option>
              {SEDES_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </CustomSelect>
          }
        />
      </div>

      {/* ── Modal: Configurar Prorrateo ──────────────────────── */}
      {showConfigModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="px-6 py-5 flex justify-between items-center" style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_TEAL} 100%)` }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Settings size={18} color="#fff" />
                </div>
                <div>
                  <h3 className="text-white font-bold m-0 text-base">Distribución por Sede</h3>
                  <p className="text-white/60 text-xs m-0">Sede: <strong className="text-white/90">{sedeConfiguracion}</strong></p>
                </div>
              </div>
              <button onClick={() => setShowConfigModal(false)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all">✕</button>
            </div>

            <div className="px-6 pt-5 pb-3">
              <p className="text-sm text-gray-500 m-0">Define cuántas personas hay en cada departamento de esta sede. El costo se dividirá proporcionalmente.</p>
            </div>

            <div className="px-6 pb-2 grid gap-3 items-center" style={{ gridTemplateColumns: '1fr 168px 32px' }}>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Departamento</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 text-center">Personas</span>
              <span />
            </div>

            <div className="px-6 pb-4 space-y-2 max-h-[45vh] overflow-y-auto overflow-x-hidden slim-scrollbar">
              {distribuciones.map((dist, idx) => {
                const totalPersonas = distribuciones.reduce((s, d) => s + (d.cantidad_personas || 0), 0);
                const pct = totalPersonas > 0 ? ((dist.cantidad_personas || 0) / totalPersonas * 100).toFixed(0) : 0;
                const color = DEPT_COLORS[idx % DEPT_COLORS.length];
                return (
                  <div key={idx} className="grid gap-3 items-center p-3 rounded-xl border border-gray-100 bg-gray-50/60 hover:bg-gray-50 transition-all" style={{ gridTemplateColumns: '1fr 168px 32px', borderLeft: `3px solid ${color}` }}>
                    <CustomSelect
                      value={dist.departamento}
                      onChange={e => { const c = [...distribuciones]; c[idx].departamento = e.target.value; setDistribuciones(c); }}
                      className="h-9 px-3 bg-white border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none focus:border-blue-400 text-gray-700"
                    >
                      <option value="">Seleccionar departamento...</option>
                      {CENTROS_COSTO_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </CustomSelect>

                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { const c = [...distribuciones]; c[idx].cantidad_personas = Math.max(1, (c[idx].cantidad_personas || 1) - 1); setDistribuciones(c); }} className="w-7 h-9 rounded-md bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 flex items-center justify-center font-bold text-sm flex-shrink-0">−</button>
                        <input
                          type="number" min="1" value={dist.cantidad_personas}
                          onChange={e => { const c = [...distribuciones]; c[idx].cantidad_personas = parseInt(e.target.value) || 1; setDistribuciones(c); }}
                          className="w-11 text-center px-1 h-9 bg-white border border-gray-200 rounded-md text-xs font-bold focus:outline-none focus:border-blue-400"
                        />
                        <button onClick={() => { const c = [...distribuciones]; c[idx].cantidad_personas = (c[idx].cantidad_personas || 1) + 1; setDistribuciones(c); }} className="w-7 h-9 rounded-md bg-white border border-gray-200 text-gray-500 hover:bg-gray-100 flex items-center justify-center font-bold text-sm flex-shrink-0">+</button>
                      </div>
                      <span className="text-[10px] font-bold px-1.5 py-1 rounded-full flex-shrink-0 text-center" style={{ color, background: `${color}18`, minWidth: 34 }}>{pct}%</span>
                    </div>

                    <button onClick={() => { const c = [...distribuciones]; c.splice(idx, 1); setDistribuciones(c); }} className="w-8 h-8 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors">
                      <FaTrash size={12} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="px-6 pb-4">
              <button onClick={() => setDistribuciones([...distribuciones, { departamento: '', cantidad_personas: 1 }])} className="flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-xl border-2 border-dashed w-full justify-center transition-all hover:border-solid" style={{ borderColor: `${SURA_TEAL}50`, color: SURA_TEAL }}>
                <FaPlus size={10} /> Añadir Departamento
              </button>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
              <button onClick={() => setShowConfigModal(false)} className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100">Cancelar</button>
              <button onClick={handleSaveConfig} className="px-5 py-2 text-sm font-bold text-white rounded-xl shadow-md hover:shadow-lg transition-all" style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_TEAL} 100%)` }}>Guardar Configuración</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal: Registrar Nuevo Enlace ───────────────────── */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-6 py-5 flex justify-between items-center" style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_TEAL} 100%)` }}>
              <h3 className="text-white font-bold m-0 flex items-center gap-2"><FaPlus size={14} /> Registrar Nuevo Enlace de Internet</h3>
              <button onClick={() => setShowAddModal(false)} className="text-white/70 hover:text-white text-lg font-bold">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Proveedor <span className="text-red-500">*</span></label>
                <input type="text" placeholder="Ej: Digitel, Net Uno, PNS..." className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevoProveedor} onChange={e => setNuevoProveedor(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Sede / Ubicación</label>
                <CustomSelect className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400" value={nuevaSede} onChange={e => setNuevaSede(e.target.value)}>
                  <option value="">Seleccionar sede...</option>
                  {SEDES_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </CustomSelect>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Departamento (Opcional)</label>
                <CustomSelect className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400" value={nuevoDepartamento} onChange={e => setNuevoDepartamento(e.target.value)}>
                  <option value="">Sin asignar (se puede editar después)</option>
                  {CENTROS_COSTO_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </CustomSelect>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
              <button onClick={() => setShowAddModal(false)} className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100">Cancelar</button>
              <button onClick={handleCreateEnlace} className="px-5 py-2 text-sm font-bold text-white rounded-xl shadow-md hover:shadow-lg transition-all" style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_TEAL} 100%)` }}>Registrar Enlace</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <ClearMonthModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmClearMonth}
        mes={mesSeleccionado}
        anio={anioSeleccionado}
        tipo="Internet"
      />

      <ConfirmDeleteModal
        isOpen={enlaceAEliminar !== null}
        onClose={() => setEnlaceAEliminar(null)}
        onConfirm={() => handleDeleteEnlace(enlaceAEliminar)}
        title="Eliminar enlace"
        message="¿Seguro que deseas eliminar este enlace de internet?"
      />

      <TendenciaAnualModal
        isOpen={trendItem !== null}
        onClose={() => setTrendItem(null)}
        titulo={trendItem?.titulo}
        subtitulo={trendItem?.subtitulo}
        anio={anioSeleccionado}
        data={trendItem?.data || []}
      />
    </div>
  );
};

export default PresupuestoInternetView;
